import axios from "axios";
import { toast } from "react-toastify";
import { DOMAIN_NAME, SKILLBREW_APP_API_BASE_URL } from "../Config/Config";
import { CODE, VALIDATION_MESSAGE } from "../Constants/Constants";
import { deleteCookie, getCookie } from "../utils/Helper";
import { ApiConfig } from "../Config/CommonConfig";

const apiinstance = axios.create({
  baseURL: SKILLBREW_APP_API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const apiinstanceFormData = axios.create({
  baseURL: SKILLBREW_APP_API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
  withCredentials: true,
});

const stripAuthorizationHeader = (config: any) => {
  if (config?.headers) {
    delete config.headers.Authorization;
  }
};

const applyRequestHeaders = (config: any) => {
  stripAuthorizationHeader(config);

  if (typeof window !== "undefined") {
    const org_id = getCookie("orgid");
    if (org_id) {
      config.headers["X-Organization-Id"] = org_id;
    }
  }

  return config;
};

apiinstance.interceptors.request.use(applyRequestHeaders);
apiinstanceFormData.interceptors.request.use(applyRequestHeaders);

const ensureCsrfToken = async (): Promise<string | null> => {
  try {
    const response = await apiinstance.get(ApiConfig?.Csrf, {
      withCredentials: true,
      validateStatus: (status) => status >= 200 && status < 600,
    });
    return response?.data?.data?.csrf_token ?? null;
  } catch {
    return null;
  }
};

let isRefreshing = false;
let failedRequestsQueue: {
  resolve: (value?: unknown) => void;
  reject: (err: any) => void;
}[] = [];

const redirectToLogin = () => {
  localStorage.clear();
  deleteCookie("user_id", "/", DOMAIN_NAME);
  deleteCookie("orgid", "/", DOMAIN_NAME);
  deleteCookie("admintoken", "/", DOMAIN_NAME);
  window.location.href = "/login";
};

const refreshToken = async () => {
  const csrf = await ensureCsrfToken();
  if (!csrf) {
    throw new Error("Failed to refresh token");
  }
  const response = await apiinstance.post(
    ApiConfig?.Refresh_Token,
    {},
    {
      withCredentials: true,
      headers: { "X-CSRFToken": csrf },
    },
  );
  if (response?.status === 200) {
    return true;
  }
  throw new Error("Failed to refresh token");
};

const handleTokenExpiration = async (
  originalRequestConfig: any,
  isSessionProbe: boolean = false,
) => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedRequestsQueue.push({ resolve, reject });
    })
      .then(() => {
        stripAuthorizationHeader(originalRequestConfig);
        return apiinstance(originalRequestConfig);
      })
      .catch((err) => Promise.reject(new Error(err)));
  }

  isRefreshing = true;

  try {
    await refreshToken();

    failedRequestsQueue.forEach(({ resolve }) => resolve());
    failedRequestsQueue = [];

    stripAuthorizationHeader(originalRequestConfig);

    return apiinstance(originalRequestConfig)
      .then((res) => res)
      .catch((error) => Promise.reject(new Error(error)));
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));

    failedRequestsQueue.forEach(({ reject }) => reject(error));
    failedRequestsQueue = [];

    if (!isSessionProbe) {
      redirectToLogin();
    }

    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
};

const DataPromise = (
  promise: any,
  toastStatus: boolean,
  isAuth: boolean = false,
  isSessionProbe: boolean = false,
) => {
  const dataPromise = promise
    .then((response: any) => response)
    .then((response: any) => {
      if (
        response?.status === CODE?.OK_CODE ||
        response?.status === CODE?.CREATED
      ) {
        if (response?.status) {
          if (toastStatus) {
            toast.success(response.data?.message);
          }
          return response.data;
        } else if (toastStatus) {
          toast.error(response.data?.message);
          return response.data;
        }
      } else if (response?.status === CODE?.ACCEPTED) {
        if (toastStatus) {
          toast.success(response.data?.message);
        }
        return response;
      } else if (response?.status === CODE?.NO_CONTENT) {
        if (toastStatus) {
          toast.success(response.data?.message);
        }
        return response;
      } else if (response?.status === CODE?.BAD_REQUEST_CODE) {
        if (toastStatus) {
          toast.error(response.data?.message);
        }
        return response?.data;
      } else if (response?.status === CODE?.CONFLICT) {
        if (toastStatus) {
          toast.success(response.data?.message);
        }
        return response.data;
      } else if (response?.status === CODE?.UNAUTHORIZED_CODE) {
        toast.error(response.data?.message);
        window.location.href = "/";
      } else if (response?.status === CODE?.EXPIRE_ACCESSTOKEN_CODE) {
        if (response?.config && (isSessionProbe || !isAuth)) {
          return handleTokenExpiration(response?.config, isSessionProbe)
            .then((res) => res?.data)
            .catch((err) => {
              if (isSessionProbe) {
                return response?.data;
              }
              return Promise.reject(
                err instanceof Error ? err : new Error(String(err)),
              );
            });
        } else if (!isAuth) {
          redirectToLogin();
        } else {
          if (toastStatus) {
            toast.error(response.data?.message);
          }
          return response?.data;
        }
      } else if (response?.status === CODE?.NOT_FOUND) {
        if (toastStatus) {
          if (response.data?.message) {
            toast.error(response.data?.message);
          } else {
            toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
          }
        }
        return response?.data;
      } else if (response?.status === CODE?.INTERNAL_SERVER_ERROR) {
        toast.error(response.data?.message);
        return response?.data;
      } else if (response?.status === CODE?.VALIDATION_CODE) {
        toast.error(response.data?.message);
        return response?.data;
      } else if (response?.status === CODE?.CONFLICT) {
        toast.error(response.data?.message);
      } else if (response?.status === CODE?.TIMEOUT) {
        toast.error(response.data?.message);
      } else if (response?.status === CODE?.UNPROCESSABLE_CONTENT) {
        toast.error(response.data?.message);
      } else {
        toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
      }
    })
    .catch(() => {
      if (!isSessionProbe) {
        toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
      }
    });
  return dataPromise;
};

export const POSTAPI = (
  URL: string,
  params: any,
  toastStatus: boolean = true,
) => {
  const promise = apiinstance.post(URL, params, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  if (URL === ApiConfig?.Login) {
    return DataPromise(promise, toastStatus, true);
  }
  return DataPromise(promise, toastStatus);
};

export const PUTAPI = (
  URL: string,
  params: any,
  toastStatus: boolean = true,
) => {
  const promise = apiinstance.put(URL, params, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus);
};

export const POSTASFORMDATA = (
  URL: string,
  params: any,
  toastStatus: boolean = false,
) => {
  const promise = apiinstanceFormData.post(URL, params, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus);
};

export const GETAPI = (URL: string, toastStatus: boolean = false) => {
  const isLoginDetail = URL === ApiConfig?.LoginDetail;
  const promise = apiinstance.get(URL, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus, isLoginDetail, isLoginDetail);
};

export const PATCHAPI = (URL: string, params: any, toastStatus: any = true) => {
  const promise = apiinstance.patch(URL, params, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus);
};

export const PATCHASFORMDATA = (
  URL: string,
  params: any,
  toastStatus: boolean = true,
) => {
  const promise = apiinstanceFormData.patch(URL, params, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus);
};

export const DELETEAPI = (URL: string, toastStatus: boolean = true) => {
  const promise = apiinstance.delete(URL, {
    validateStatus: (status) => status >= 200 && status < 600,
  });
  return DataPromise(promise, toastStatus);
};
